// store/api/crmApi.ts
// RTK Query API for CRM integrations (HubSpot, Salesforce, Dynamics 365)

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  CRMDetailsResponse,
  TrialPeriodResponse,
  ConnectCRMPayload,
  SaveFieldMappingPayload,
  CRMOwnersResponse,
  CRMOwner,
  CRMApiResponse,
  CRMSyncResponse,
  DispositionListResponse,
  FieldMappingData,
  CRMAvailableField,
} from '@/lib/types/crm-types';
import { CDN_ASSETS } from '@/lib/cdn';

// ========================================
// CRM TYPE & CONFIG
// ========================================

/**
 * CRM Types supported by CL-FE
 */
export type CRMType = 'hubspot' | 'salesforce' | 'dynamic';

/**
 * CRM Authentication types
 */
export type CRMAuthType = 'oauth' | 'apikey';

/**
 * CRM configuration metadata
 */
export interface CRMConfig {
  name: string;
  type: CRMType;
  displayName: string;
  authType: CRMAuthType;
  logo: string;
  width: number;
  height: number;
  supportsLeadFields: boolean;
  brandColor: string;
  /** One-line summary of what connects/syncs, shown on the settings card. */
  description: string;
  /** Marks this CRM as coming soon — disabled with a badge. */
  comingSoon?: boolean;
}

/**
 * CRM configurations
 */
export const CRM_CONFIGS: Record<CRMType, CRMConfig> = {
  hubspot: {
    name: 'hubspot',
    type: 'hubspot',
    displayName: 'HubSpot',
    authType: 'oauth',
    logo: CDN_ASSETS.crm.hubspot,
    width: 113,
    height: 44,
    supportsLeadFields: false,
    brandColor: '#ff7a59',
    description: 'Sync contacts and companies with HubSpot CRM.',
  },
  salesforce: {
    name: 'salesforce',
    type: 'salesforce',
    displayName: 'Salesforce',
    authType: 'oauth',
    logo: CDN_ASSETS.crm.salesforce,
    width: 166,
    height: 44,
    supportsLeadFields: false,
    brandColor: '#00a1e0',
    description: 'Sync contacts and companies with Salesforce.',
  },
  dynamic: {
    name: 'dynamic',
    type: 'dynamic',
    displayName: 'Dynamics 365',
    authType: 'oauth',
    logo: CDN_ASSETS.crm.dynamics365,
    width: 150,
    height: 44,
    supportsLeadFields: false,
    brandColor: '#002050',
    description: 'Sync contacts and companies with Microsoft Dynamics 365.',
    comingSoon: true,
  },
};

// ========================================
// API ENDPOINTS
// ========================================

export const CRM_ENDPOINTS = {
  getCrmDetails: (userId: string) => `/api/crm/save_zoho_refresh_token/${userId}`,
  connectCrm: (crmType: CRMType, userId: string) =>
    `/api/crm/add_comp_to_hubspot/${crmType}/${userId}`,
  addToCrm: (crmType: CRMType, userId: string) =>
    `/api/crm/select_contact_for_crm/${crmType}/${userId}`,
  getOwners: (userId: string, crmFlag: string) =>
    `/api/crm/owners/${userId}/${crmFlag}`,
  getDispositionList: () => `/api/crm/dispositionlist`,
  syncCrm: (crmType: CRMType, userId: string) =>
    `/api/crm/sync/${crmType}/${userId}`,
  getFieldMapping: (crmType: CRMType, userId: string) =>
    `/api/crm/${crmType}/crm_fields_mapping/${crmType}/${userId}`,
  saveFieldMapping: (crmType: CRMType, userId: string) =>
    `/api/crm/${crmType}/crm_fields_mapping/${crmType}/${userId}`,
  hubspotAuthCallback: '/api/crm/hubspot/AuthHandler',
  salesforceAuthCallback: '/api/crm/salesforce/AuthHandler',
  dynamicAuthCallback: '/api/crm/dynamic/dynamicAuthHandler',
} as const;

// ========================================
// OAUTH CONFIGURATION
// ========================================

export const CRM_OAUTH_CONFIG = {
  hubspot: {
    authUrl: 'https://app.hubspot.com/oauth/authorize',
    scopes: [
      'crm.objects.contacts.read',
      'crm.objects.contacts.write',
      'crm.objects.companies.read',
      'crm.objects.companies.write',
      'crm.objects.deals.read',
      'crm.objects.deals.write',
      'crm.objects.owners.read',
    ],
    getRedirectUri: () => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      return hostname === 'localhost'
        ? 'http://localhost:3000/api/crm/hubspot/AuthHandler'
        : `https://${hostname}/api/crm/hubspot/AuthHandler`;
    },
  },
  salesforce: {
    authUrl: 'https://login.salesforce.com/services/oauth2/authorize',
    scopes: ['api', 'refresh_token', 'offline_access'],
    getRedirectUri: () => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      return hostname === 'localhost'
        ? 'http://localhost:3000/api/crm/salesforce/AuthHandler'
        : `https://${hostname}/api/crm/salesforce/AuthHandler`;
    },
  },
  dynamic: {
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scopes: ['openid', 'profile', 'offline_access', 'https://org.crm.dynamics.com/.default'],
    getRedirectUri: () => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      return hostname === 'localhost'
        ? 'http://localhost:3000/api/crm/dynamic/dynamicAuthHandler'
        : `https://${hostname}/api/crm/dynamic/dynamicAuthHandler`;
    },
  },
} as const;

// ========================================
// RAW FIELD MAPPING RESPONSE TYPES
// ========================================

export interface RawFieldMappingResponse {
  res: {
    status?: string;
    companyLabelType?: { label: string; type: string }[];
    contactLabelType?: { label: string; type: string }[];
    leadLabelType?: { label: string; type: string }[];
    company_labels?: string[];
    contact_labels?: string[];
    lead_labels?: string[];
    company_map_dict?: Record<string, string | null>;
    contact_map_dict?: Record<string, string | null>;
    lead_map_dict?: Record<string, string | null>;
  };
}

export interface TransformedFieldMapping {
  companyFields: { label: string; type: string }[];
  contactFields: { label: string; type: string }[];
  leadFields: { label: string; type: string }[];
  companyMapping: Record<string, string | null>;
  contactMapping: Record<string, string | null>;
  leadMapping: Record<string, string | null>;
  error?: string;
}

// ========================================
// TRANSFORM FUNCTIONS
// ========================================

function transformFieldMappingResponse(raw: any): TransformedFieldMapping {
  const res = raw?.res || raw || {};

  if (res.status === 'error') {
    return {
      companyFields: [],
      contactFields: [],
      leadFields: [],
      companyMapping: {},
      contactMapping: {},
      leadMapping: {},
      error: 'Failed to load field mapping data',
    };
  }

  const companyFields = res.companyLabelType || (res.company_labels || []).map((l: string) => ({ label: l, type: 'string' }));
  const contactFields = res.contactLabelType || (res.contact_labels || []).map((l: string) => ({ label: l, type: 'string' }));
  const leadFields = res.leadLabelType || (res.lead_labels || []).map((l: string) => ({ label: l, type: 'string' }));

  return {
    companyFields,
    contactFields,
    leadFields,
    companyMapping: res.company_map_dict || {},
    contactMapping: res.contact_map_dict || {},
    leadMapping: res.lead_map_dict || {},
  };
}

function transformCrmDetailsResponse(raw: any): CRMDetailsResponse[] {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    const hasValidTypes = raw.length > 0 && raw.every((d: any) => d.crmType && d.crmType in CRM_CONFIGS);
    if (hasValidTypes) return raw;
    if (raw.length === 1 && raw[0] && typeof raw[0] === 'object') {
      return transformCrmDetailsResponse(raw[0]);
    }
  }

  const activeCrmType = (raw.user_crm_status || '').toLowerCase();
  const isActive = raw.crm_active === 'active' || raw.crm_active === true;
  const accountStatus = raw.crm_account_status;
  const mappingStatus = raw.feildMappingStatus || raw.fieldMappingStatus || false;

  const allCrmTypes = Object.keys(CRM_CONFIGS) as CRMType[];

  return allCrmTypes.map((crmType): CRMDetailsResponse => {
    const isThisCrm = activeCrmType === crmType;
    const connected = isThisCrm && isActive && accountStatus !== false && accountStatus !== 'Credentials Expired';

    return {
      crmType,
      connected,
      accountStatus: {
        crm_account_status: isThisCrm ? accountStatus : false,
        crm_active: isThisCrm && isActive,
        connected_crm_flag: isThisCrm && connected,
        mapping_status: isThisCrm ? mappingStatus : false,
      },
    };
  });
}

// ========================================
// CACHE CONFIGURATION
// ========================================

const CACHE_DURATIONS = {
  SHORT: 60,
  MEDIUM: 300,
  LONG: 600,
} as const;

const CRM_TAGS = {
  CrmDetails: 'CrmDetails',
  FieldMapping: 'FieldMapping',
  Owners: 'Owners',
  TrialStatus: 'TrialStatus',
  SyncStatus: 'SyncStatus',
} as const;

// ========================================
// RTK QUERY API
// ========================================

export const crmApi = createApi({
  reducerPath: 'crmApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: (headers) => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: [
    CRM_TAGS.CrmDetails,
    CRM_TAGS.FieldMapping,
    CRM_TAGS.Owners,
    CRM_TAGS.TrialStatus,
    CRM_TAGS.SyncStatus,
  ],
  keepUnusedDataFor: CACHE_DURATIONS.MEDIUM,
  endpoints: (build) => ({
    // ========================================
    // CRM DETAILS & CONNECTION
    // ========================================

    /**
     * Get CRM details (all CRM statuses for user)
     */
    getCrmDetails: build.query<CRMDetailsResponse[], { userId: string }>({
      query: ({ userId }) => ({
        url: CRM_ENDPOINTS.getCrmDetails(userId),
        method: 'GET',
      }),
      transformResponse: (response: any) => transformCrmDetailsResponse(response),
      providesTags: [CRM_TAGS.CrmDetails],
    }),

    /**
     * Connect/Disconnect CRM
     */
    connectCrm: build.mutation<
      any,
      { crmType: CRMType; userId: string; payload: ConnectCRMPayload }
    >({
      query: ({ crmType, userId, payload }) => ({
        url: CRM_ENDPOINTS.connectCrm(crmType, userId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CRM_TAGS.CrmDetails],
    }),

    // ========================================
    // FIELD MAPPING
    // ========================================

    /**
     * Get field mapping for a CRM
     */
    getFieldMapping: build.query<
      TransformedFieldMapping,
      { crmType: CRMType; userId: string }
    >({
      query: ({ crmType, userId }) => ({
        url: CRM_ENDPOINTS.getFieldMapping(crmType, userId),
        method: 'GET',
      }),
      transformResponse: (response: any) => transformFieldMappingResponse(response),
      providesTags: [CRM_TAGS.FieldMapping],
    }),

    /**
     * Save field mapping for a CRM
     */
    saveFieldMapping: build.mutation<
      any,
      { crmType: CRMType; userId: string; payload: SaveFieldMappingPayload }
    >({
      query: ({ crmType, userId, payload }) => ({
        url: CRM_ENDPOINTS.saveFieldMapping(crmType, userId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CRM_TAGS.FieldMapping, CRM_TAGS.CrmDetails],
    }),

    // ========================================
    // CRM OWNERS
    // ========================================

    /**
     * Get CRM owners/users
     */
    getCrmOwners: build.query<CRMOwnersResponse, { userId: string; crmFlag: string }>({
      query: ({ userId, crmFlag }) => ({
        url: CRM_ENDPOINTS.getOwners(userId, crmFlag),
        method: 'GET',
      }),
      providesTags: [CRM_TAGS.Owners],
    }),

    /**
     * Lazy query for fetching owners on demand
     */
    getLazyCrmOwners: build.query<CRMOwnersResponse, { userId: string; crmFlag: string }>({
      query: ({ userId, crmFlag }) => ({
        url: CRM_ENDPOINTS.getOwners(userId, crmFlag),
        method: 'GET',
      }),
      providesTags: [CRM_TAGS.Owners],
    }),

    // ========================================
    // CRM SYNC & EXPORT
    // ========================================

    /**
     * Add contacts/companies to CRM
     */
    /**
     * Pushes the selected records into the connected CRM.
     *
     * Body shape is the upstream contract, not ours: the id list is keyed by
     * object type (`contactIds` vs `companyIds`) rather than a generic array,
     * the requesting user's name and email ride along, and `pageFlag` tells
     * the service which surface the selection came from - saved records are
     * `radarContacts`, live search hits are `contactSearch`. Sending a generic
     * `selectedIds` with none of that, as this did, is not something the
     * service can act on.
     */
    addToCrm: build.mutation<
      CRMApiResponse,
      {
        crmType: CRMType;
        userId: string;
        selectedIds: string[];
        entityType: 'contacts' | 'companies';
        /** Which tab the selection came from — decides `pageFlag`. */
        tab: 'all' | 'saved';
        owner: CRMOwner;
        addAs?: 'Contact' | 'Lead';
        user?: { firstName?: string; lastName?: string; email?: string };
      }
    >({
      query: ({ crmType, userId, selectedIds, entityType, tab, owner, addAs = 'Contact', user }) => ({
        url: CRM_ENDPOINTS.addToCrm(crmType, userId),
        method: 'POST',
        body: {
          ...(entityType === 'companies'
            ? { companyIds: selectedIds }
            : { contactIds: selectedIds }),
          owner,
          addAs,
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          email: user?.email ?? '',
          pageFlag: tab === 'saved' ? 'radarContacts' : 'contactSearch',
        },
      }),
      invalidatesTags: [CRM_TAGS.SyncStatus],
    }),

    /**
     * Sync CRM data
     */
    syncCrmData: build.mutation<CRMSyncResponse, { crmType: CRMType; userId: string }>({
      query: ({ crmType, userId }) => ({
        url: CRM_ENDPOINTS.syncCrm(crmType, userId),
        method: 'GET',
      }),
      invalidatesTags: [CRM_TAGS.SyncStatus],
    }),

    /**
     * Get disposition list
     */
    getDispositionList: build.query<DispositionListResponse, void>({
      query: () => ({
        url: CRM_ENDPOINTS.getDispositionList(),
        method: 'GET',
      }),
    }),

    /**
     * Check trial period
     */
    checkTrialPeriod: build.mutation<TrialPeriodResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `/api/crm/trial_check/${userId}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: [CRM_TAGS.TrialStatus],
    }),
  }),
});

export const {
  useGetCrmDetailsQuery,
  useConnectCrmMutation,
  useGetFieldMappingQuery,
  useSaveFieldMappingMutation,
  useGetCrmOwnersQuery,
  useLazyGetCrmOwnersQuery,
  useAddToCrmMutation,
  useSyncCrmDataMutation,
  useGetDispositionListQuery,
  useCheckTrialPeriodMutation,
} = crmApi;
